import React, { useState } from 'react';
import {
  SalvBanner,
  SalvBannerType,
  SalvButton,
  SalvModal,
  SalvTagsInput,
  SalvTextField,
} from '@salvapp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../../feedback/feedbackReducer';
import { parseErrorMessage } from '../../common/errors/helpers';
import { useRerunScenariosForTransactionsByIdRange } from './scenarioRerunActions';

interface Props {
  show: boolean;
  onClose: () => void;
}

interface FormValues {
  scenarioHandles: string[];
  minId: string;
  maxId: string;
}

const validationSchema = Yup.object().shape({
  scenarioHandles: Yup.array()
    .of(Yup.string().required())
    .min(1, 'At least one scenario handle is required')
    .required(),
  minId: Yup.number().typeError('Must be a number').required('Min ID is required'),
  maxId: Yup.number().typeError('Must be a number').required('Max ID is required'),
});

const RerunByTxIdRangeModal = ({ show, onClose }: Props) => {
  const dispatch = useDispatch();
  const mutation = useRerunScenariosForTransactionsByIdRange();
  const [error, setError] = useState<string | null>(null);

  const initialValues: FormValues = { scenarioHandles: [], minId: '', maxId: '' };

  const handleSubmit = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
    setError(null);
    try {
      await mutation.mutateAsync({
        scenarioHandles: values.scenarioHandles,
        minId: Number(values.minId),
        maxId: Number(values.maxId),
      });
      dispatch(
        showFeedback({ message: 'Scenario rerun by transaction ID range triggered successfully', success: true })
      );
      onClose();
      resetForm();
    } catch (e) {
      setError(await parseErrorMessage(e));
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} validateOnMount onSubmit={handleSubmit}>
      {({ values, errors, isValid, isSubmitting, setFieldValue, resetForm }) => (
        <SalvModal
          show={show}
          onHide={() => {
            resetForm();
            setError(null);
            onClose();
          }}
        >
          <Form>
            <SalvModal.Header>Rerun Scenarios for Transactions by ID Range</SalvModal.Header>
            <SalvModal.Body>
              {error && <SalvBanner message={error} type={SalvBannerType.DANGER} clearable className='m-b-16' />}
              <SalvTagsInput
                value={values.scenarioHandles}
                onChange={(newValues) => setFieldValue('scenarioHandles', newValues)}
                label='Scenario Handles'
                placeholder='Enter scenario handle'
                isInvalid={!!errors.scenarioHandles}
                className='m-b-16'
              />
              <SalvTextField
                label='Min ID'
                value={values.minId}
                onChange={(value: string) => setFieldValue('minId', value)}
                className='m-b-16'
              />
              <SalvTextField
                label='Max ID'
                value={values.maxId}
                onChange={(value: string) => setFieldValue('maxId', value)}
              />
            </SalvModal.Body>
            <SalvModal.Footer>
              <SalvButton
                variant='muted'
                onClick={() => {
                  resetForm();
                  setError(null);
                  onClose();
                }}
                label='Cancel'
              />
              <SalvButton label='Rerun' type='submit' disabled={!isValid || isSubmitting || mutation.isPending} />
            </SalvModal.Footer>
          </Form>
        </SalvModal>
      )}
    </Formik>
  );
};

export default RerunByTxIdRangeModal;
